import { useCallback, useEffect, useRef } from 'react';

import { useForm } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form';
import type { IntlShape } from 'react-intl';

import { validatePersonForm } from './personFormSchema';
import { TPersonFormValues, TSerialisedPeople } from './PersonModal.types';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';

function getVisibleErrors(
    errors: Record<string, { type: string; message: string }>,
    values: Record<string, any>,
    country: string
) {
    const isGuarantor = !!values.isGuarantor;

    if (!(isGuarantor && country === 'DE')) {
        delete errors.additionalGuarantorConsent;
    }

    if (!(isGuarantor || country === 'DE')) {
        delete errors.email;
        Object.keys(errors).forEach((key) => {
            if (key.startsWith('phones.')) delete errors[key];
        });
    }

    return errors;
}

export function usePersonForm(
    defaultValues: TPersonFormValues,
    people: TSerialisedPeople,
    intl: IntlShape
) {
    const peopleRef = useRef(people);
    const hasSubmittedRef = useRef(false);

    useEffect(() => {
        peopleRef.current = people;
    }, [people]);

    const methods = useForm<TPersonFormValues>({
        defaultValues
    });

    // Re-validate on change after first failed submission to clear resolved errors
    useEffect(() => {
        const subscription = methods.watch(() => {
            if (!hasSubmittedRef.current) return;

            const values = methods.getValues();
            const country = getCountry();
            const errors = getVisibleErrors(
                validatePersonForm(values as unknown as Record<string, any>, {
                    intl,
                    people: peopleRef.current,
                    uid: values.uid,
                    country
                }),
                values as unknown as Record<string, any>,
                country
            );

            methods.clearErrors();
            for (const [path, error] of Object.entries(errors)) {
                methods.setError(path as FieldPath<TPersonFormValues>, error);
            }
        });
        return () => subscription.unsubscribe();
    }, [methods, intl]);

    const handleSave = useCallback(
        (onValid: (data: TPersonFormValues) => void) => {
            hasSubmittedRef.current = true;
            const values = methods.getValues();
            methods.clearErrors();

            const country = getCountry();

            const errors = getVisibleErrors(
                validatePersonForm(values as unknown as Record<string, any>, {
                    intl,
                    people: peopleRef.current,
                    uid: values.uid,
                    country
                }),
                values as unknown as Record<string, any>,
                country
            );

            if (Object.keys(errors).length === 0) {
                onValid(values);
                return;
            }

            for (const [path, error] of Object.entries(errors)) {
                methods.setError(path as FieldPath<TPersonFormValues>, error);
            }
        },
        [methods, intl]
    );

    return { ...methods, handleSave };
}
